import { RoutePaths } from "@/core/constants";
import { useAuth } from "@/core/hooks/useAuth";
import { checkPasswordStrength } from "@/shared/utils/password";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import log from "loglevel";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { encodeEmailConfirmationToken } from "@/shared/utils/emailConfirmationToken";
import {
  identifyUser,
  capture,
  setUserProperties,
  registerSuperProperties,
} from "@/shared/lib/analytics";
import { resolveOnboardingVariant } from "@/shared/utils/onboardingVariant";
import { resolveOrigin } from "@/shared/utils/salesEntry";

export type SignUpForm = {
  email: string;
  password: string;
  passwordConfirmation: string;
};

export const useSignUpController = () => {
  const navigate = useNavigate();
  const { register } = useAuth();

  const [loading, { open: openLoading, close: closeLoading }] =
    useDisclosure(false);

  const form = useForm<SignUpForm>({
    initialValues: {
      email: "",
      password: "",
      passwordConfirmation: "",
    },
    validateInputOnBlur: true,

    validate: {
      email: (value) => {
        const cleanedValue = value.trim();
        if (!cleanedValue) {
          return "E-mail é obrigatório";
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(cleanedValue) ? null : "E-mail inválido";
      },

      password: (value) => {
        const cleanedValue = value.trim();
        if (!cleanedValue) {
          return "Senha é obrigatória";
        }
        const { isValid } = checkPasswordStrength(cleanedValue);

        if (!isValid) {
          return "A senha não atende a todos os requisitos";
        }

        return null;
      },

      passwordConfirmation: (value, values) => {
        const cleanedValue = value.trim();

        if (!cleanedValue) {
          return "Confirmação de senha é obrigatória";
        }

        const cleanedPassword = values.password.trim();
        return cleanedValue === cleanedPassword
          ? null
          : "As senhas devem ser idênticas";
      },
    },
  });

  useEffect(() => {
    notifications.hide("sector-disconnected");
  }, []);

  const handleSignUp = async () => {
    openLoading();
    try {
      // SA-1217: resolvido ANTES do register — o register consome o modo do
      // localStorage no sucesso (uso único), então ler depois devolveria
      // `self_serve` para uma conta que veio de link de vendas.
      const origin = resolveOrigin();

      const newUserRegistered = await register(
        form.values.email.trim(),
        form.values.password.trim()
      );

      // Identidade consistente com o resto do app (AuthProvider identifica por
      // `documentId`). Usar o `id` numérico aqui criava um person_id separado no
      // PostHog e o `signup_completed` não linkava aos eventos seguintes do
      // mesmo usuário (person split) — quebrando funis por pessoa no signup.
      await identifyUser(newUserRegistered.documentId!, {
        email: newUserRegistered.email,
      });

      // SA-1160: aloca a variante do experimento de onboarding (with_card vs.
      // without_card) via feature flag e persiste como person property +
      // super property, para todo evento client-side carregar a variante.
      const onboardingVariant = resolveOnboardingVariant();
      // SA-1216: `origin` (self_serve × sales) vai junto, ANTES do capture — o
      // `signup_completed` é o primeiro passo do funil por pessoa e não carrega o
      // group da Company (ela ainda não existe), então sem a super property aqui
      // o funil perde a dimensão na entrada.
      await setUserProperties({
        onboarding_variant: onboardingVariant,
        origin,
      });
      registerSuperProperties({
        onboarding_variant: onboardingVariant,
        origin,
      });

      // SA-1165: `method` padroniza a origem da conta (email/google/invite)
      // e habilita breakdown do topo do funil por método.
      capture("signup_completed", { method: "email" });

      const token = encodeEmailConfirmationToken({
        email: newUserRegistered.email,
        id: String(newUserRegistered.id),
      });

      navigate(`${RoutePaths.EMAIL_CONFIRMATION}?token=${token}`, {
        replace: true,
      });

      setTimeout(() => {
        notifications.show({
          title: "Cadastro efetuado com sucesso!",
          message: "Vamos confirmar seu e-mail!",
          color: "teal",
          autoClose: 3000,
        });
      }, 100);
    } catch (error) {
      log.error(error);
      const typedError = error as {
        response?: { data?: { error?: { message?: string } } };
      };

      const apiMessage = typedError.response?.data?.error?.message;

      if (apiMessage === "Email or Username are already taken") {
        notifications.show({
          title: "Este e-mail já está cadastrado",
          message: "Faça login para continuar ou recupere sua senha",
          color: "yellow",
          autoClose: 5000,
        });
      } else {
        notifications.show({
          title: "Erro no cadastro",
          message: "Tente novamente em instantes.",
          color: "red",
          autoClose: 4000,
        });
      }
    } finally {
      closeLoading();
      form.reset();
    }
  };

  return {
    form,
    loading,
    handleSignUp,
  };
};
